import { ExceptionTypes } from '~/src/components/shared/features/types'

import { RouteOption } from '@loadboard/DetailView/components/RouteDropdown/types'
import { LoadInput, StopInput } from '@loadboard/Modals/UndoStopsModal/types'
import { TRACKING_SOURCE } from '@loadboard/constants'

import {
  BackhaulEligibleCode,
  BillTo,
  Entity,
  FreightRecordCreate,
  LoadNoteCreateRequest,
  ReorderCompletedStopsRequest,
  ScosStopAction,
  StopLateReason,
  StopRescheduleRequest,
  TenderLoadRequest,
} from '~/__generated_types__/globalTypes'
import {
  GetLoadsQueryVariables,
  GetStopEstimateQuery,
  UpdateExceptionMutation,
} from '~/src/components/Operational/LoadBoardDashboard/__generated_types__/queries.gql'

import { ValidDriver } from '~/src/components/Operational/LoadBoardDashboard/Modals/AssignDriverToLegModalRV2/types'
import {
  Carrier,
  DetailedLoad,
  StopLocation,
  Stop,
  CostCenterWithCode,
  StopUpdateBase,
  StopAddBase,
  StopDuplicateBase,
  LegWithEstimates,
  Tractor,
  OverlappingLoadsForTractor,
  OverlappingLoadsForTrailer,
  LoadReference,
  ManualArriveDepartStops,
  EquipmentMove,
  Trailer,
  RyderNumber,
  TrackingDataRescheduleStop,
  Route,
  RouteStops,
  StopLateReason as CostCenterStopLateReason,
} from '~/src/components/Operational/LoadBoardDashboard/types'
import {
  Note,
  Unit,
  UnitType,
} from '~/src/components/Operational/LoadBoardDashboard/DetailView/types'
import { Driver } from '~/src/components/Operational/LoadBoardDashboard/Modals/AssignDriverToLegModal/types'
import { ServerErrorType } from '~/src/components/Operational/LoadBoardDashboard/Modals/ManualArriveDepart/types'

export enum EditType {
  ExecutedByCarrier = 'Carrier',
  ExecutedByDriver = 'Driver',
}

export type InputReference = {
  type: string
  value: string
  stopId?: bigint
  freightRecordId?: number
}

export type SearchResult = {
  label?: Group
  options: Array<Option>
  meta?: Meta | null
}

export type Group = {
  groupName: string
  type: SearchType
}

export type Option = {
  id: number
  name: string
  type?: string
  subType?: string
}

export type Meta = {
  total: number
  type: SearchType
}

type SearchState = {
  searchResults: Array<SearchResult>
  reset: () => void
  loading: boolean
}

export enum SearchType {
  CustomerReference = 'CustomerReferenceNumber',
  Reference = 'Reference',
  StopReference = 'StopReference',
  FreightRecordReference = 'FreightRecordReference',
  FreightRecordBarcode = 'FreightRecordBarcode',
}

export type UseSearchReturnType = [
  getSearchResult: ({ value, type }: { value: string; type?: string }) => void,
  state: SearchState
]

export type UseSearchTractorOptions = {
  costCenterCode?: string
  searchByBusinessUnitCostCenter?: boolean
  includeRetiredTractors?: boolean
}

export type UseSearchTractorReturnType = [
  getFuzzyMatchTractors: (value: string) => void,
  state: {
    defaultData: Array<Tractor>
    matchedData: Array<Tractor> | undefined
    loading: boolean
    abortSearch: () => void
  }
]

export type UseSearchTrailerOptions = {
  costCenterId: number
  searchByBusinessUnitCostCenter?: boolean
}

export type UseSearchTrailerReturnType = [
  getFuzzyMatchTrailers: (value: string) => void,
  state: {
    abortSearch: () => void
    defaultData: Array<Trailer>
    matchedData: Array<Trailer>
    loading: boolean
  }
]

/**
 * Search Location
 */
export type LocationSearchState = {
  allLocations: Array<StopLocation>
  matchedLocations: Array<StopLocation>
  loading: boolean
}

export type UseSearchLocationReturnType = [
  getSearchLocations: (value: string) => void,
  state: LocationSearchState
]

export type UseSearchStopAndMileageTypesReturnType = (value: number | undefined) => void

export type UseGetStopLateReasonsReturnType = {
  lateReasons: Array<CostCenterStopLateReason>
  loading: boolean
}

export type UseUpdateStopReturnType = [
  updateStop: (stopUpdate: StopUpdateBase) => void,
  state: { loading: boolean }
]

export type UseDeleteStopReturnType = [
  deleteStop: (stop: Stop) => void,
  state: { loading: boolean }
]

export type useGetOverlappingLoadsOrEquipmentMovesForDriverReturnType = [
  getOverlappingLoadsOrEquipmentMoves: (driver: Driver, tractorId?: number) => void,
  state: {
    isLoading: boolean
  }
]

export type useGetOverlappingLoadsForTractorReturnType = [
  getOverlappingLoads: (tractorId: number, legId: number) => void,
  state: {
    loading: boolean
    overlappingLoads: OverlappingLoadsForTractor | undefined
  }
]

export type useGetOverlappingLoadsForTrailerReturnType = [
  getOverlappingLoads: (trailerId: number) => void,
  state: {
    loading: boolean
    overlappingLoads: OverlappingLoadsForTrailer | undefined
  }
]

export type useGetEquipmentMoveReturnType = [
  getEquipmentMoves: ({
    tractorId,
    onEquipmentMove,
  }: {
    tractorId: number | null
    onEquipmentMove: (data: EquipmentMove) => void
  }) => void,
  state: {
    loading: boolean
  }
]

export type useGetOverlappingLoadsOrEquipmentMoveReturnType = [
  getOverlappingLoadsOrEquipmentMove: (tractor: Tractor) => void,
  state: {
    loading: boolean
    overlappingLoads: OverlappingLoadsForTractor | undefined
    equipmentMove: EquipmentMove | undefined
  }
]

export type useGetDriversForAssignModalReturnType = [
  getFuzzyMatchDrivers: (value: string) => void,
  state: {
    loading: boolean
    abortSearch: () => void
    data: Array<Driver>
  }
]

export type useGetDriversForAssignModalReturnTypeV2 = [
  getFuzzyMatchDrivers: (value: string) => void,
  state: {
    loadingDefaultData: boolean
    loadingSearch: boolean
    abortSearch: () => void
    defaultData: Array<Driver> | undefined
    fuzzyMatchedData:
      | {
          currentCostCenter: Array<Driver>
          otherCostCenters: Array<Driver>
        }
      | undefined
  }
]

export type UseReorderStopReturnType = [
  reorderStop: (fromIdx: number, toIdx: number, onError?: () => void) => void,
  state: { loading: boolean }
]

export enum ErrorType {
  InvalidStopAdd = 'InvalidStopAdd',
  InvalidStopEvent = 'InvalidStopEvent',
  InvalidStopType = 'InvalidStopType',
  InvalidStopAppointmentTime = 'InvalidStopAppointmentTime',
  InvalidStopAppointmentEnd = 'InvalidStopAppointmentEnd',
  InvalidStopAppointmentStart = 'InvalidStopAppointmentStart',
  InvalidStopDateTime = 'InvalidStopDateTime',
  InvalidOdometer = 'InvalidOdometer',
  InvalidStopLocation = 'InvalidStopLocation',
}

export type AntonErrorType = {
  type: ErrorType[keyof ErrorType]
  message: string | undefined
}

export type UseAddStopReturnType = [
  addStop: (stop: StopAddBase) => void,
  state: {
    loading: boolean
    addAntonError: AntonErrorType | undefined
  }
]

export type UseAddLayoverStopReturnType = [
  addLayoverStop: (input: LayoverStopAddInput) => void,
  state: {
    loading: boolean
    serverError: AntonErrorType | undefined
  }
]

export type GpsPointInput = { lat: number; lon: number }

export type LayoverStopAddInput = {
  legId: number
  idx: number
  appointmentStart?: string
  plannedDwell?: number
  locationId?: bigint
  gpsPoint?: GpsPointInput
  arrivalTime?: string
  departureTime?: string
  tractorMileage?: number
}

export type UseDuplicateStopReturnType = [
  duplicateStop: (stop: StopDuplicateBase, stopIdx: number) => void,
  state: {
    loading: boolean
    duplicateAntonError: AntonErrorType | undefined
  }
]

export type UseCreateLoadReturnType = [
  createLoad: (
    costCenter: CostCenterWithCode,
    route: Route,
    plannedStartTime: string,
    source: TRACKING_SOURCE
  ) => void,
  state: { loading: boolean }
]

export type UseCreateNoteReturnType = [
  createLoad: (note: LoadNoteCreateRequest) => void,
  state: { loading: boolean }
]

export type UseDeleteNoteReturnType = [
  deleteNote: (note: Note) => void,
  state: { loading: boolean }
]

type FixedRouteSearchState = {
  fixedRoutes: Array<Route>
  loading: boolean
}

export type UseSearchFixedRoutesReturnType = [
  getFixedRoutesCostCenter: (costCenterCode: string) => void,
  searchFixedRoutes: (costCenterCode: string, searchString: string) => void,
  state: FixedRouteSearchState
]

type FixedRouteStopsState = {
  routeStops: RouteStops | undefined
  loading: boolean
}

export type UseGetFixedRouteStopsReturnType = [
  getFixedRouteStops: (routeId: number) => void,
  state: FixedRouteStopsState
]

export type UseUpdateLegStartTimeReturnType = [
  updateLegStartTime: (startTime: string) => void,
  state: { loading: boolean }
]

export type useResourceChangeReturnType = [
  resourceChange: (
    legId: number,
    stopIdx: number,
    locationId: bigint | undefined,
    note?: string
  ) => void,
  state: { loading: boolean }
]

export type AddTrailerChangeParams = {
  legId: number
  stopIdx: number
  locationId: bigint | undefined
  note?: string
}

export type UseAddTrailerChangeReturnType = [
  addTrailerChange: (params: AddTrailerChangeParams) => void,
  state: { loading: boolean }
]

export type UseDeleteTrailerChangeReturnType = [
  deleteTrailerChange: (stop: Stop) => void,
  state: { loading: boolean }
]

export type UseAssignTractorReturnType = [
  assignTractor: (leg: LegWithEstimates, tractor: Tractor | undefined) => void
]

export type UseAssignTrailerReturnType = [assignTrailer: (trailer?: Trailer) => void]

export type UseAssignTrailerToStopReturnType = [
  assignTrailer: (stop: Stop, trailer?: Trailer) => void
]

export type UseAssignCarrierReturnType<TLoad> = [
  assignCarrier: (load: TLoad, legId: number, carrier: Carrier | null) => void
]

export type UseUpdateBackhaulCodeReturnType = [
  updateBackhaulCode: (backhaulCode: BackhaulEligibleCode) => void,
  state: { loading: boolean }
]

export type UseGetLoadsReturnType = [
  getLoads: (variables: GetLoadsQueryVariables) => void,
  state: {
    isLoadingInitial: boolean
    isLoadingDetails: boolean
    clearSearch: () => void
  }
]

export type DispatchLoad = {
  loadId: number
  trackingLocation: string
  tmwVersionId: bigint
  version: string
  tmwOrder: string | undefined | null
  costCenterName: string | undefined | null
  costCenterCode: string | undefined | null
  noteBody?: string
  tenderStatus?: string | null
  loadStatus: string | undefined
  autoDispatchEligible?: boolean
}

export type UseDispatchLoadReturnType = [
  ({
    loadId,
    trackingLocation,
    tmwVersionId,
    version,
    tmwOrder,
    costCenterName,
    costCenterCode,
  }: DispatchLoad) => void,
  {
    loading: boolean
    loadingLoadId: number | null
  }
]

export type MarkLoadAsCompleted = {
  load: Pick<
    DetailedLoad,
    'status' | 'ryderNumber' | 'costCenter' | 'id' | 'autoCompleteScheduledTime'
  >
  note?: string
  onSuccess?: () => void
}

export type UseMarkLoadCompletedReturnType = [
  markLoadCompleted: ({ load, note, onSuccess }: MarkLoadAsCompleted) => void,
  state: {
    loading: boolean
    success: boolean
    loadingLoadId: number | null
  }
]

export type UseCancelLoadReturnType<TLoad> = [
  cancelLoad: (load: TLoad, source: TRACKING_SOURCE, note?: string) => void
]

export type UseWithdrawLoadTenderReturnType = [
  withdrawLoadTender: (loadId: number, reason?: string) => void,
  state: { loading: boolean }
]

/**
 * Reference CRUD
 */
export type UseDeleteReferenceReturnType = [
  deleteReference: (reference: LoadReference, entity: Entity) => void,
  state: { loading: boolean }
]

export type UseUpdateReferenceReturnType = [
  updateReference: (reference: LoadReference, entity: Entity) => void,
  state: { loading: boolean }
]

export type UseCreateReferenceReturnType = [
  createReference: (reference: InputReference, entity: Entity) => void,
  state: { loading: boolean }
]

export type UseUpdateBillToReturnType = [
  updateBillTo: (billTo: BillTo) => void,
  state: { loading: boolean }
]

export type useManualArriveDepartReturnType = [
  manualArriveDepart: (input: ManualArriveDepartStops, type: EditType) => void,
  state: {
    loading: boolean
    serverError: ServerErrorType | undefined
    resetError: () => void
  }
]

export type UseResetLoadReturnType = [
  resetLoad: (loadId: number) => void,
  state: { loading: boolean; success: boolean }
]

export type UseDeleteFreightRecordReturnType = [
  deleteFreightRecord: (freightRecordId: number) => void,
  state: { loading: boolean }
]
export type UseCreateFreightRecordReturnType = [
  createFreightRecord: (input: FreightRecordCreate) => void,
  state: { loading: boolean }
]

export type FreightRecordInput = {
  id: number
  value: number | null
  unit: UnitType | null
  type: Unit | null
  stopId?: bigint
}

export type UseUpdateFreightRecordReturnType = [
  updateFreightRecord: ({ id, value, unit, type, stopId }: FreightRecordInput) => void,
  state: { loading: boolean }
]

export type useGetOverlappingLoadsForDriverReturnTypeRV2 = [
  getOverlappingLoads: (
    driver: ValidDriver,
    legId: number,
    isOtherCostCenter: boolean
  ) => void,
  state: {
    loadingOverlappingLegs: boolean
  }
]

export type UseMergeLegReturnType = [
  (loadId: number, legIdx: number) => void,
  { loading: boolean }
]

export type UseUpdateRouteReturnType = [
  updateRoute: (route: RouteOption) => void,
  state: { loading: boolean }
]

export type SetStopTypeAction = {
  type: 'SET_STOP_TYPE'
  payload?: string | null
}
export type ClearStopTypeAndScosActionAction = {
  type: 'CLEAR'
}
export type SetScosActionAction = {
  type: 'SET_SCOS_ACTION'
  payload?: ScosStopAction | null
}

export type UseUpdateOsdRecordReturnType = [
  updateBarcode: (osdRecordId: number, updatedBarcode: string) => void,
  state: { loading: boolean }
]

export type UseCreateOsdRecordReturnType = [
  createOsdRecord: (barcode: string) => void,
  state: { loading: boolean }
]

export type UseSearchRyderNumberReturnType = [
  getSearchRyderNumber: (ryderNumber: string, costCenterId: number) => void,
  state: {
    loading: boolean
    searchResults: {
      result: Array<RyderNumber>
    }
    reset: () => void
  }
]

export type UseRescheduleStopReturnType = [
  rescheduleStop: (
    input: StopRescheduleRequest,
    trackingPropries: TrackingDataRescheduleStop
  ) => void,
  state: { loading: boolean }
]

export type UseSkipStopReturnType = [
  skipStop: (stopId: bigint) => Promise<unknown>,
  state: { loading: boolean }
]

export type UseAttemptStopReturnType = [
  attemptStop: () => void,
  state: { loading: boolean }
]

export type UseReorderCompletedStopReturnType = [
  reorderCompletedStop: (input: ReorderCompletedStopsRequest) => void,
  state: {
    loading: boolean
    serverEventError: bigint | null | undefined
    resetError: () => void
  }
]

export type UseTenderLoadReturnType = [
  tenderLoad: (input: TenderLoadRequest) => void,
  state: { loading: boolean }
]

export type UseUndoStopsReturnType = [
  undoStops: (input: StopInput | LoadInput) => void,
  state: { loading: boolean }
]

type StopEstimateResult = Extract<
  NonNullable<GetStopEstimateQuery['getStopEstimate']>,
  { __typename?: 'StopEstimate' }
>

export type StopEstimateQueryInput = {
  loadId: number
  legId: number
  stopIdx: number
  arrivalTime: string
  departureTime: string
  locationId?: bigint
}

export type UseGetStopEstimateReturnType = {
  getStopEstimate: (input: StopEstimateQueryInput) => void
  estimateData: StopEstimateResult['estimateData'] | undefined
  stopLocation: StopEstimateResult['stopLocation'] | undefined
  loading: boolean
}

export type UpdateExceptionInput = {
  exception: ExceptionTypes
  stopLateReason?: StopLateReason
  description?: string
  isReviewed?: boolean
  shouldCascadeReason?: boolean
}

export type UpdateExceptionSuccessLoad = Extract<
  UpdateExceptionMutation['updateException'],
  { __typename: 'ExceptionUpdated' }
>

export type useUpdateExceptionReturnType = [
  updateException: (input: UpdateExceptionInput) => void,
  state: { loading: boolean }
]
